import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ClipboardList, ArrowRight, Loader2, User, Trash2 } from 'lucide-react';
import { formDraftApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Dashboard widget that shows the current user's in-progress form drafts
 * (started-but-unsubmitted). Clicking "Resume" reopens the draft in the fill
 * page so they can finish where they left off.
 *
 * Uses `GET /api/form-drafts` which is already scoped per-role on the server
 * — non-admin users only see their own drafts; practice admins see the org's.
 */
export default function FormsInProgressWidget({ max = 5 }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await formDraftApi.list();
        if (!cancelled) setDrafts(res.data?.drafts || []);
      } catch {
        if (!cancelled) setDrafts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const discard = async (draft, e) => {
    // Stop the click from bubbling up into the Resume <Link>.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!window.confirm(`Discard this draft of "${draft.form_name || 'the form'}"? This can't be undone.`)) return;
    try {
      setDeletingId(draft.id);
      await formDraftApi.delete(draft.id);
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
      toast.success('Draft discarded');
    } catch {
      toast.error('Could not discard draft');
    } finally { setDeletingId(null); }
  };

  const visible = drafts.slice(0, max);

  return (
    <Card className="careiq-card" data-testid="forms-in-progress-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#0A2342] flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#CA8A04]" />
          {t('dashboard.formsInProgress')}
          <span className="ml-auto text-xs font-normal text-[#64748B]" data-testid="forms-in-progress-count">
            {loading ? '' : `${drafts.length} ${drafts.length === 1 ? t('common.draft') : t('common.drafts')}`}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" />
          </div>
        ) : drafts.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-6" data-testid="forms-in-progress-empty">
            No forms in progress. Start filling a form and it will show up here so you can resume later.
          </p>
        ) : (
          <div className="space-y-2">
            {visible.map((d) => {
              const resumeHref = d.patient_id
                ? `/forms/${d.form_id}/fill?patientId=${d.patient_id}&draftId=${d.id}`
                : `/forms/${d.form_id}/fill?draftId=${d.id}`;
              const savedLabel = d.updated_at
                ? new Date(d.updated_at).toLocaleString()
                : '';
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors"
                  data-testid={`forms-in-progress-row-${d.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-[#0A2342] truncate">{d.form_name || 'Untitled form'}</div>
                    <div className="flex items-center gap-2 text-[11px] text-[#64748B] mt-0.5">
                      {d.patient_name && (
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3 h-3" />{d.patient_name}
                        </span>
                      )}
                      <span className="text-[#94A3B8]">· {d.fields_filled || 0} field{d.fields_filled === 1 ? '' : 's'} filled</span>
                      {savedLabel && <span className="text-[#94A3B8]">· last saved {savedLabel}</span>}
                    </div>
                  </div>
                  <Link to={resumeHref} data-testid={`forms-in-progress-resume-${d.id}`}>
                    <Button size="sm" className="careiq-btn-primary h-8 text-xs">
                      {t('common.resume')}<ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => discard(d, e)}
                    disabled={deletingId === d.id}
                    title="Discard this draft"
                    aria-label="Discard draft"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[#94A3B8] hover:text-[#991B1B] hover:bg-[#FEE2E2] transition-colors disabled:opacity-40"
                    data-testid={`forms-in-progress-discard-${d.id}`}
                  >
                    {deletingId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
            {drafts.length > max && (
              <p className="text-[11px] text-[#94A3B8] text-center pt-1">
                + {drafts.length - max} more not shown
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
